/**
 * 전역 스타일 및 CSS 리셋
 * 모던 웹앱을 위한 최적화된 전역 스타일
 */

import { createGlobalStyle } from 'styled-components';
import { colors, typography, animations } from './designTokens';

export const GlobalStyles = createGlobalStyle`
  /* CSS Reset & Normalize */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* HTML & Body */
  html {
    font-size: 16px;
    line-height: 1.5;
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  body {
    font-family: ${typography.fontFamily.sans};
    font-size: ${typography.fontSize.base};
    font-weight: ${typography.fontWeight.normal};
    line-height: ${typography.lineHeight.normal};
    color: ${colors.semantic.text.primary};
    background-color: ${colors.semantic.background.secondary};
    overflow-x: hidden;
    transition: background-color ${animations.duration.normal} ${animations.easing.smooth};
  }

  /* Root */
  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${typography.fontWeight.semibold};
    line-height: ${typography.lineHeight.tight};
    color: ${colors.semantic.text.primary};
    margin: 0;
  }

  h1 {
    font-size: ${typography.fontSize['4xl']};
    font-weight: ${typography.fontWeight.bold};
  }

  h2 {
    font-size: ${typography.fontSize['3xl']};
  }

  h3 {
    font-size: ${typography.fontSize['2xl']};
  }

  h4 {
    font-size: ${typography.fontSize.xl};
  }

  h5 {
    font-size: ${typography.fontSize.lg};
  }

  h6 {
    font-size: ${typography.fontSize.base};
  }

  /* Paragraphs */
  p {
    margin: 0;
    line-height: ${typography.lineHeight.relaxed};
    color: ${colors.semantic.text.secondary};
  }

  /* Links */
  a {
    color: ${colors.primary[600]};
    text-decoration: none;
    transition: color ${animations.duration.fast} ${animations.easing.smooth};
    
    &:hover {
      color: ${colors.primary[700]};
      text-decoration: underline;
    }
    
    &:focus {
      outline: 2px solid ${colors.primary[500]};
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  /* Lists */
  ul, ol {
    list-style: none;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Form Elements */
  input, textarea, select, button {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  input, textarea, select {
    border: 1px solid ${colors.semantic.border.primary};
    border-radius: 6px;
    padding: 0.75rem 1rem;
    font-size: ${typography.fontSize.sm};
    transition: border-color ${animations.duration.fast} ${animations.easing.smooth},
                box-shadow ${animations.duration.fast} ${animations.easing.smooth};
    
    &:focus {
      outline: none;
      border-color: ${colors.semantic.border.focus};
      box-shadow: 0 0 0 3px ${colors.primary[100]};
    }
    
    &:disabled {
      background-color: ${colors.semantic.background.tertiary};
      color: ${colors.semantic.text.disabled};
      cursor: not-allowed;
    }
    
    &::placeholder {
      color: ${colors.semantic.text.tertiary};
    }
  }

  /* Tables */
  table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  th, td {
    text-align: left;
    padding: 0.75rem;
    border-bottom: 1px solid ${colors.semantic.border.primary};
  }

  th {
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.semantic.text.primary};
    background-color: ${colors.semantic.background.tertiary};
  }

  /* Code */
  code, pre {
    font-family: ${typography.fontFamily.mono};
    font-size: ${typography.fontSize.sm};
  }

  code {
    background-color: ${colors.semantic.background.tertiary};
    padding: 0.125rem 0.25rem;
    border-radius: 3px;
    color: ${colors.error[600]};
  }

  pre {
    background-color: ${colors.semantic.background.tertiary};
    padding: 1rem;
    border-radius: 6px;
    overflow-x: auto;
    line-height: ${typography.lineHeight.relaxed};
  }

  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.semantic.background.tertiary};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.semantic.border.secondary};
    border-radius: 4px;
    transition: background-color ${animations.duration.fast} ${animations.easing.smooth};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.semantic.text.tertiary};
  }

  /* Selection */
  ::selection {
    background-color: ${colors.primary[200]};
    color: ${colors.primary[900]};
  }

  /* Focus Visible */
  :focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* Reduced Motion */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  /* High Contrast Mode */
  @media (prefers-contrast: high) {
    :root {
      --border-opacity: 1;
      --shadow-opacity: 1;
    }
  }

  /* Print Styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }
    
    a, a:visited {
      text-decoration: underline;
    }
    
    a[href]:after {
      content: " (" attr(href) ")";
    }
    
    abbr[title]:after {
      content: " (" attr(title) ")";
    }
    
    pre, blockquote {
      border: 1px solid ${colors.neutral[500]};
      page-break-inside: avoid;
    }
    
    thead {
      display: table-header-group;
    }
    
    tr, img {
      page-break-inside: avoid;
    }
    
    img {
      max-width: 100% !important;
    }
    
    p, h2, h3 {
      orphans: 3;
      widows: 3;
    }
    
    h2, h3 {
      page-break-after: avoid;
    }
  }

  /* Utility Classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .not-sr-only {
    position: static;
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }

  /* Loading States */
  .loading {
    opacity: 0.6;
    pointer-events: none;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2px solid ${colors.primary[200]};
      border-top-color: ${colors.primary[500]};
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  /* Error States */
  .error {
    color: ${colors.error[600]};
    background-color: ${colors.error[50]};
    border: 1px solid ${colors.error[200]};
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: ${typography.fontSize.sm};
  }

  /* Success States */
  .success {
    color: ${colors.success[600]};
    background-color: ${colors.success[50]};
    border: 1px solid ${colors.success[200]};
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: ${typography.fontSize.sm};
  }

  /* Warning States */
  .warning {
    color: ${colors.warning[600]};
    background-color: ${colors.warning[50]};
    border: 1px solid ${colors.warning[200]};
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: ${typography.fontSize.sm};
  }

  /* Animation Keyframes */
  ${animations.keyframes.fadeIn}
  ${animations.keyframes.slideUp}
  ${animations.keyframes.slideDown}
  ${animations.keyframes.scaleIn}
  ${animations.keyframes.spin}
  ${animations.keyframes.pulse}
`;
